/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useCallback, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import { useFormik } from 'formik';
import _ from 'lodash';
import ReactPlayer from 'react-player';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SelectInput from '../../components/SelectInput';
import { EntrepreneurIntro, Industry, EntIndustryList, MarketModel, marketModelList } from '../../types/types';
import FileUploader from '../../components/FileUploader';
import Form from '../../components/Form';
import GeographySelect from '../../components/GeographySelect';
import IndustrySelect from '../../components/IndustrySelect';
import { formikSubmitDisabled } from '../../lib/utils';
import { entPublishedIntroSchema, entUnpublishedIntroSchema } from '../../lib/validationSchemas';
import { StyledText } from '../../components/StyledText';
import { PitchPanelItem } from './EntrepreneurPitchPanel';


const getValidationSchema = (isPublished: boolean) => (isPublished ? entPublishedIntroSchema : entUnpublishedIntroSchema);

const initialValues = (profile: EntrepreneurIntro) => ({
  name: profile.name || '',
  intro: profile.intro || '',
  geography: profile.geography,
  marketModelList: profile.marketModelList || [],
  linkedinProfileUrl: profile.linkedinProfileUrl || '',
  pitchDeckUrl: profile.pitchDeckUrl || '',
  executiveSummaryUrl: profile.executiveSummaryUrl || '',
  fullPitchUrl: profile.fullPitchUrl || '',
  elevatorPitchUrl: profile.elevatorPitchUrl || '',
  industryList: profile.industryList || []
});

interface EntrepreneurIntroEditProps {
  introPanelBody: EntrepreneurIntro
  focusedItem: PitchPanelItem
  profileId: number
  isPublished: boolean
  handleClose: () => void
  handleSubmit: (values: EntrepreneurIntro) => void
}

const EntrepreneurIntroEdit: React.FC<EntrepreneurIntroEditProps> = ({
  introPanelBody,
  focusedItem,
  isPublished,
  profileId,
  handleSubmit,
  handleClose
}) => {
  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(introPanelBody),
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  });

  const validateVideoUrl: (value: string) => string | void = (value) => {
    const urlIsEmpty = value === '';

    if (urlIsEmpty) {
      return undefined;
    }

    const error = 'A valid Youtube or Vimeo video url is required';

    const isNotYoutube = value.search('youtube') < 0 && value.search('youtu.be') < 0;
    const isNotVimeo = value.search('vimeo') < 0;

    if (isNotYoutube && isNotVimeo) {
      console.log('returning error');
      return error;
    }

    const canPlay = ReactPlayer.canPlay(value);
    if (!canPlay) {
      return error;
    }
    return undefined;
  };

  useEffect(() => {
    if (focusedItem) {
      formik.setSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedItem]);

  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };

  const focusedRef = useCallback((node: HTMLDivElement) => {
    if (node != null) {
      node.scrollIntoView();
    }
  }, []);

  const industryList = EntIndustryList;

  return (
    <Dialog
      open={focusedItem !== PitchPanelItem.None}
      onClose={handleClose}
      disableRestoreFocus
    >
      <Form formik={formik}>
        <DialogTitle disableTypography ref={(focusedItem && focusedItem === PitchPanelItem.General) ? focusedRef : undefined}>
          <StyledText variant='h2'>Edit General Information</StyledText>
        </DialogTitle>
        <DialogContent>
          <TextInput
            type='text'
            fullWidth
            id='name'
            label='Project / Company Name'
            placeholder='Name of the company'
            autoFocus={focusedItem === PitchPanelItem.General}
          />
          <TextInput
            type='text'
            fullWidth
            id='intro'
            label='Short Project Description'
            placeholder='Short Description (max. 1500 characters)'
            multiline
            rows={5}
            maxLength={1500}
          />
          <GeographySelect
            id='geography'
            labelId='geography'
            label='Location'
            showAnyCase={false}
            value={formik.values.geography}
            error={formik.errors.geography}
            touched={formik.touched.geography}
            onBlur={formik.handleBlur}
          />
          <SelectInput
            id='marketModelList'
            label='Market Model (select all that apply)'
            multiple
            value={formik.values.marketModelList}
            error={formik.errors.marketModelList}
            touched={formik.touched.marketModelList}
            onBlur={formik.handleBlur}
            options={marketModelList.map((i: MarketModel) => ({ value: i, label: i }))}
          />
          <IndustrySelect
            id='industryList'
            label='Industries (select up to 5)'
            multiple
            onBlur={formik.handleBlur}
            touched
            value={formik.values.industryList}
            error={formik.errors.industryList}
            options={industryList.map((i: Industry) => ({ value: i, label: i.industry }))}
          />
          <TextInput
            type='text'
            fullWidth
            id='elevatorPitchUrl'
            label='Link to Elevator Pitch (optional)'
            placeholder=''
            validate={validateVideoUrl}
            autoFocus={(focusedItem && focusedItem === PitchPanelItem.Elevator) || false}
          />
          <TextInput
            type='text'
            fullWidth
            id='linkedinProfileUrl'
            label='Link to CEO’s Linkedin'
            placeholder=''
            autoFocus={(focusedItem && focusedItem === PitchPanelItem.LinkedIn) || false}
          />
          <div ref={(focusedItem && focusedItem !== PitchPanelItem.General) ? focusedRef : undefined}>
            <FileUploader
              label='Pitch Deck'
              id='pitchDeckUrl'
              actionLabel='Upload Pitch Deck'
              fileUrl={introPanelBody.pitchDeckUrl}
              filePath={`entrepreneur/${profileId}/`}
              fileTypes='.ppt, pptx'
              autoFocus={focusedItem === PitchPanelItem.PitchDeck}
              callback={(url) => {
                if (url === null) {
                  formik.setFieldValue('pitchDeckUrl', '');
                } else {
                  const urlOjb = new URL(url);
                  formik.setFieldValue('pitchDeckUrl', 'https://' + urlOjb.hostname + urlOjb.pathname);
                }
              }}
            />
            <br />
            <FileUploader
              label='Executive Summary'
              id='executiveSummaryUrl'
              actionLabel='Upload Executive Summary'
              fileUrl={introPanelBody.executiveSummaryUrl}
              filePath={`entrepreneur/${profileId}/`}
              fileTypes='.doc, .docx, .pdf'
              autoFocus={focusedItem === PitchPanelItem.ExecutiveSummary}
              callback={(url) => {
                if (url === null) {
                  formik.setFieldValue('executiveSummaryUrl', '');
                } else {
                  const urlOjb = new URL(url);
                  formik.setFieldValue('executiveSummaryUrl', 'https://' + urlOjb.hostname + urlOjb.pathname);
                }
              }}
            />
          </div>
          <TextInput
            type='text'
            fullWidth
            id='fullPitchUrl'
            label='Link to Full Pitch (optional)'
            placeholder=''
            validate={validateVideoUrl}
            autoFocus={(focusedItem && focusedItem === PitchPanelItem.FullPitch) || false}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            type='button'
            variant='contained'
          >
            Cancel
          </Button>
          {formik.isSubmitting ? <CircularProgress />
            : (
              <Button
                type='submit'
                variant='contained'
                color='primary'
                disabled={formikSubmitDisabled(formik)}
              >
                Update
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EntrepreneurIntroEdit;
